import type { CommandDeckContinueResolution } from '../lib/resolveCommandDeckContinue'
import type {
  CommandDeckPreviewBundle,
  PrimarySurface,
  StageSnapshot,
  ToolAvailability,
} from '../contracts/types'

export type SpatialMapNodeStatus =
  | 'active'
  | 'ready'
  | 'preview'
  | 'coming_soon'
  | 'locked'
  | 'boundary'

export type SpatialMapNode = {
  id: string
  label: string
  stageId: string
  surfaceId: PrimarySurface
  status: SpatialMapNodeStatus
  statusLabel: string
  angleDeg: number
  isFocus: boolean
  isActiveSurface: boolean
  isRouteTarget: boolean
  inspectDetail: string
}

export type SpatialMapModel = {
  projectName: string
  nodes: SpatialMapNode[]
  routeLabel: string | null
  focusSummary: string
}

const LAYER_DEFS: {
  id: string
  label: string
  stageId: string
  surfaceId: PrimarySurface
}[] = [
  { id: 'guided_studio', label: 'Guided Studio', stageId: 'structure_coherent', surfaceId: 'guided_studio' },
  { id: 'story_framed', label: 'Story Framed', stageId: 'intent_framed', surfaceId: 'guided_studio' },
  { id: 'world_defined', label: 'World Defined', stageId: 'world_or_scape_defined', surfaceId: 'guided_studio' },
  {
    id: 'characters_grounded',
    label: 'Characters Grounded',
    stageId: 'character_grounded',
    surfaceId: 'guided_studio',
  },
  {
    id: 'screening',
    label: 'Screening',
    stageId: 'omnicast_presentation_ready',
    surfaceId: 'omnicast',
  },
  { id: 'xr_extension', label: 'XR Extension', stageId: 'xr_expansion_ready', surfaceId: 'xr_worlds' },
]

const STATUS_LABELS: Record<SpatialMapNodeStatus, string> = {
  active: 'Active',
  ready: 'Ready',
  preview: 'Preview',
  coming_soon: 'Soon',
  locked: 'Locked',
  boundary: 'Read-only',
}

function surfaceFromContinuePath(path: string | null): PrimarySurface | null {
  if (!path) return null
  const base = path.split('?')[0] ?? path
  if (base === '/guided-studio' || base.startsWith('/guided-studio/')) return 'guided_studio'
  if (base.startsWith('/omnistudio')) return 'omnistudio'
  if (base.startsWith('/omnicine')) return 'omnicine'
  if (base.startsWith('/omnicast')) return 'omnicast'
  if (base.startsWith('/guided/start-from-world')) return 'scapes_worlds'
  if (base.startsWith('/wearables')) return 'xr_worlds'
  return null
}

function toolAvailability(bundle: CommandDeckPreviewBundle, surfaceId: PrimarySurface): ToolAvailability {
  const tool = bundle.tools.find((t) => t.toolId === surfaceId)
  return tool?.availability ?? 'locked'
}

function statusFromStageAndTool(
  stage: StageSnapshot | undefined,
  toolAvail: ToolAvailability,
  isFocus: boolean
): SpatialMapNodeStatus {
  if (isFocus) return 'active'
  if (!stage) {
    if (toolAvail === 'available' || toolAvail === 'beta') return 'ready'
    if (toolAvail === 'preview') return 'preview'
    if (toolAvail === 'coming_soon') return 'coming_soon'
    return 'locked'
  }
  if (stage.status === 'ready') return 'ready'
  if (stage.status === 'in_progress') return 'active'
  if (stage.status === 'blocked' || stage.status === 'needs_review') return 'locked'
  if (stage.status === 'locked' || stage.status === 'deferred') return 'locked'
  if (stage.status === 'future') {
    if (toolAvail === 'coming_soon' || toolAvail === 'request_access') return 'coming_soon'
    if (toolAvail === 'preview') return 'preview'
    if (toolAvail === 'locked' || toolAvail === 'unavailable' || toolAvail === 'admin_only') {
      return 'locked'
    }
    return 'coming_soon'
  }
  return toolAvail === 'preview' ? 'preview' : 'locked'
}

function inspectDetail(
  label: string,
  status: SpatialMapNodeStatus,
  stage: StageSnapshot | undefined,
  toolAvail: ToolAvailability
): string {
  const stageLine = stage
    ? `Progress layer: ${stage.label} (${stage.status.replace(/_/g, ' ')}).`
    : 'Progress layer not yet grounded on this mission.'
  return `${label} — ${STATUS_LABELS[status]}. Surface access: ${toolAvail.replace(/_/g, ' ')}. ${stageLine} Inspection only; Command Deck does not execute workflows from this map.`
}

export function deriveSpatialMapModel(
  bundle: CommandDeckPreviewBundle,
  continueResolution: CommandDeckContinueResolution
): SpatialMapModel {
  const focusStageId = bundle.progress.currentFocusStageId
  const activeSurfaceId = bundle.personalization.surface.surfaceId
  const routeSurface = surfaceFromContinuePath(continueResolution.targetPath)
  const spine = bundle.progress.spine ?? []
  const count = LAYER_DEFS.length

  const nodes: SpatialMapNode[] = LAYER_DEFS.map((def, index) => {
    const stage = spine.find((s) => s.stageId === def.stageId)
    const toolAvail = toolAvailability(bundle, def.surfaceId)
    const isFocus = def.stageId === focusStageId
    const isActiveSurface =
      activeSurfaceId !== 'none' &&
      def.surfaceId === activeSurfaceId &&
      def.id === 'guided_studio'
    const status = statusFromStageAndTool(stage, toolAvail, isFocus)
    const isRouteTarget =
      Boolean(routeSurface && def.surfaceId === routeSurface && continueResolution.navigable)

    return {
      id: def.id,
      label: def.label,
      stageId: def.stageId,
      surfaceId: def.surfaceId,
      status,
      statusLabel: STATUS_LABELS[status],
      angleDeg: (360 / count) * index - 90,
      isFocus,
      isActiveSurface,
      isRouteTarget,
      inspectDetail: inspectDetail(def.label, status, stage, toolAvail),
    }
  })

  const focusStage = spine.find((s) => s.stageId === focusStageId)
  const focusSummary = focusStage?.label ?? bundle.bestNextMove.primary.suggestedAction

  return {
    projectName: bundle.progress.projectName,
    nodes,
    routeLabel: continueResolution.navigable ? continueResolution.label : null,
    focusSummary,
  }
}
